// KPI tile for the aggregated reserve-fund balance.
//
// Owned by task 4.3 of the dashboard-redesign spec (Requirements 7.1 and 7.2).
// See:
//   - `.kiro/specs/dashboard-redesign/design.md` §Widget table row 3
//     (`Widget_ReserveFundPosition`).
//   - `.kiro/specs/dashboard-redesign/requirements.md` §Requirement 7.
//   - `.kiro/steering/constitution.md` Rule 1 (integer pence) and Rule 2
//     (reserve funds are ring-fenced per building — aggregation is
//     informational only, never a spendable pool).
//
// This is a **read-only** presentation widget: it consumes
// `useReserveFundPosition(scope)` and never mutates. The four render
// states (loading / empty / ready / error) are handled by <WidgetFrame>;
// this component only owns the ready-state layout, plus a defensive
// <ErrorBoundary> around the whole tree so a render-time bug in the
// KPI or the ring-fence note cannot blank the surrounding page
// (design.md §Design principles: widget failure independence).

import React from "react";
import { Link } from "react-router-dom";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import type { BuildingScope, ReserveFundPosition } from "../types";
import { useReserveFundPosition } from "../hooks/useReserveFundPosition";
import { shouldShowRingFenceNote } from "../reserveFund";
import { WidgetFrame } from "../WidgetFrame";
import { WidgetError } from "../WidgetError";
import { formatMoneyGBP, getMonetaryColour } from "../formatMoney";
import { ErrorBoundary } from "../../../components/ui/ErrorBoundary";

/**
 * Route the KPI number links to for a "see everything" view of the
 * reserve fund. Directed by the task 4.3 brief; design.md only pins the
 * empty-state CTA (`/buildings/{id}/reserve-fund`), so the widget-level
 * deep-link lives here.
 */
const RESERVE_FUND_DEEP_LINK = "/dashboard/reserve-funds";

/** Fixed copy required by Requirement 7.2. */
const RING_FENCE_COPY =
  "Reserve funds are ring-fenced per building and cannot be commingled.";

/** Empty-state copy for the widget frame. */
const EMPTY_COPY = "No reserve fund data";

/** Human-readable widget name — surfaced in the scoped error card title. */
const WIDGET_NAME = "Reserve Fund";

export interface WidgetReserveFundPositionProps {
  scope: BuildingScope;
}

/**
 * Ready-state layout: label, KPI money, and the ring-fencing callout.
 *
 * Split into its own component so the parent can stay a thin state-router
 * over `<WidgetFrame>` without an inline JSX tree.
 */
function ReadyState({ data }: { data: ReserveFundPosition }): JSX.Element {
  // Reserve fund is money the building is owed by leaseholders and held in
  // trust — an Owed context per formatMoney's `MonetaryContext` (Req 4.4).
  const colour = getMonetaryColour(data.balancePence, "owed");

  // Gate the ring-fencing callout on the pure helper so this widget and
  // the P5 property test share the same truth (Req 7.2 / P5).
  const showRingFenceNote = shouldShowRingFenceNote({
    balancePence: data.balancePence,
    aggregatedFromBuildings: data.aggregatedFromBuildings,
  });

  return (
    <div
      data-testid="widget-reserve-fund-ready"
      className="w-full h-full flex flex-col gap-2 px-4 py-3"
    >
      <div
        className="text-xs font-semibold uppercase tracking-wide"
        style={{ color: "#6b7280" }}
      >
        {WIDGET_NAME}
      </div>
      <Link
        to={RESERVE_FUND_DEEP_LINK}
        data-testid="widget-reserve-fund-link"
        aria-label={`Reserve fund balance ${formatMoneyGBP(
          data.balancePence,
        )}, view details`}
        className="text-3xl font-bold hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
        style={{ color: colour }}
      >
        {formatMoneyGBP(data.balancePence)}
      </Link>
      {showRingFenceNote && (
        <div
          data-testid="widget-reserve-fund-ring-fence-note"
          role="note"
          className="flex items-start gap-1 text-xs"
          style={{ color: "#6b7280" }}
        >
          <InformationCircleIcon
            aria-hidden="true"
            className="h-4 w-4 flex-shrink-0 mt-0.5"
            style={{ color: "#6b7280" }}
          />
          <span>{RING_FENCE_COPY}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Fallback rendered by the ErrorBoundary when a descendant throws during
 * render. `error-boundary` errorId marks it as a client-side render error
 * (as opposed to a fetch-layer error, which carries a UUID from the hook).
 */
function renderBoundaryFallback(): JSX.Element {
  return <WidgetError errorId="error-boundary" name={WIDGET_NAME} />;
}

/**
 * The public widget. Passes `scope` to the hook, hands the result to
 * `<WidgetFrame>` for lifecycle routing, and only builds the ready
 * layout when the hook has data.
 */
export function Widget_ReserveFundPosition({
  scope,
}: WidgetReserveFundPositionProps): JSX.Element {
  const result = useReserveFundPosition(scope);

  return (
    <ErrorBoundary fallback={renderBoundaryFallback}>
      <WidgetFrame result={result} name={WIDGET_NAME} emptyCopy={EMPTY_COPY}>
        {result.status === "ready" ? <ReadyState data={result.data} /> : null}
      </WidgetFrame>
    </ErrorBoundary>
  );
}

export default Widget_ReserveFundPosition;
